import React from 'react';
import { ChartBar20 } from '@carbon/icons-react';
import type { Product } from '#config/products';
import type Products from '.';
import PageQuery from '#core/PageQuery';
import DataPlusLayout from './layout';

const productPage = (productName: keyof typeof Products) => async () => {
  // this cannot be a param, dont try to make this function generic
  const products = (await import('.')).default;
  return {
    default: products[productName],
  };
};

const Overview = React.lazy(productPage('Overview'));
const Pillars = React.lazy(productPage('Pillars'));
const PillarsUpsell = React.lazy(productPage('PillarsUpsell'));
const Metrics = React.lazy(productPage('Metrics'));
const MetricsUpsell = React.lazy(productPage('MetricsUpsell'));
const YearOnYear = React.lazy(productPage('YearOnYear'));
const YearOnYearUpsell = React.lazy(productPage('YearOnYearUpsell'));
const Subjects = React.lazy(productPage('Subjects'));
const SubjectsUpsell = React.lazy(productPage('SubjectsUpsell'));
const BenchmarkSummary = React.lazy(productPage('BenchmarkSummary'));
const BenchmarkSummaryUpsell = React.lazy(
  productPage('BenchmarkSummaryUpsell'),
);
const PeerGroupAnalysis = React.lazy(productPage('PeerGroupAnalysis'));
const PeerGroupAnalysisUpsell = React.lazy(
  productPage('PeerGroupAnalysisUpsell'),
);
const BibliometricAnalysis = React.lazy(productPage('BibliometricAnalysis'));
const BibliometricAnalysisUpsell = React.lazy(
  productPage('BibliometricAnalysisUpsell'),
);
const DetailedSubjects = React.lazy(productPage('DetailedSubjects'));
const DetailedSubjectsUpsell = React.lazy(
  productPage('DetailedSubjectsUpsell'),
);
const InstitutionFinder = React.lazy(productPage('InstitutionFinder'));
const InstitutionFinderUpsell = React.lazy(
  productPage('InstitutionFinderUpsell'),
);

const dataplusConfig: Product = {
  name: 'WUR Dashboard',
  authKey: 'dataplus',
  description:
    'Access the full dataset behind THE’s influential World University Rankings',
  path: 'wur',
  icon: ChartBar20,
  color: 'redOrange ',
  pageQuery: PageQuery,
  productConfig: {
    topbar: {
      title: 'World University Ranking',
      titleAcronym: 'WUR',
      filter: {
        hasPeerSelector: true,
        hasQueryBuilder: false,
      },
    },
  },
  pages: [
    {
      name: 'Overview',
      path: 'overview',
      layout: DataPlusLayout,
      component: Overview,
      pageConfig: {
        topbar: {
          downloadUrls: {
            xlsx: '/download/wur/overview/xlsx',
            csv: '/download/wur/overview/zip',
          },
          filter: {
            hasPeerSelector: false,
          },
        },
      },
    },
    {
      name: 'Benchmark Summary',
      path: 'benchmark-summary',
      layout: DataPlusLayout,
      component: BenchmarkSummary,
      upsellComponent: BenchmarkSummaryUpsell,
      pageConfig: {
        topbar: {
          downloadUrls: {
            xlsx: '/download/wur/benchmark-summary/xlsx',
            csv: '/download/wur/benchmark-summary/zip',
          },
          filter: {
            hasPeerSelector: true,
          },
        },
      },
    },
    {
      name: 'Pillars',
      path: 'pillars',
      component: Pillars,
      layout: DataPlusLayout,
      upsellComponent: PillarsUpsell,
      pageConfig: {
        topbar: {
          downloadUrls: {
            xlsx: '/download/wur/pillars/xlsx',
            csv: '/download/wur/pillars/zip',
          },
          filter: {
            hasPeerSelector: true,
          },
        },
      },
    },
    {
      name: 'Metrics',
      path: 'metrics',
      component: Metrics,
      layout: DataPlusLayout,
      upsellComponent: MetricsUpsell,
      pageConfig: {
        topbar: {
          downloadUrls: {
            xlsx: '/download/wur/metrics/xlsx',
            csv: '/download/wur/metrics/zip',
          },
          filter: {
            hasPeerSelector: true,
          },
        },
      },
    },
    {
      name: 'Bibliometric Analysis',
      path: 'bibliometric-analysis',
      component: BibliometricAnalysis,
      upsellComponent: BibliometricAnalysisUpsell,
      layout: DataPlusLayout,
      pageConfig: {
        topbar: {
          downloadUrls: {
            xlsx: '/download/wur/bibliometric-analysis/xlsx',
            csv: '/download/wur/bibliometric-analysis/zip',
          },
          filter: {
            hasPeerSelector: true,
            hasQueryBuilder: true,
          },
        },
      },
    },
    {
      name: 'Subjects',
      path: 'subjects',
      component: Subjects,
      layout: DataPlusLayout,
      upsellComponent: SubjectsUpsell,
      pageConfig: {
        topbar: {
          downloadUrls: {
            xlsx: '/download/wur/subjects/xlsx',
            csv: '/download/wur/subjects/zip',
          },
          filter: {
            hasPeerSelector: true,
          },
        },
      },
    },
    {
      name: 'Detailed Subjects',
      path: 'detailed-subjects',
      layout: DataPlusLayout,
      component: DetailedSubjects,
      upsellComponent: DetailedSubjectsUpsell,
      pageConfig: {
        topbar: {
          downloadUrls: {
            xlsx: '/download/wur/detailed-subjects/xlsx',
            csv: '/download/wur/detailed-subjects/zip',
          },
          filter: {
            hasQueryBuilder: true,
          },
        },
      },
    },
    {
      name: 'Peer Group Analysis',
      path: 'peer-group-analysis',
      component: PeerGroupAnalysis,
      layout: DataPlusLayout,
      upsellComponent: PeerGroupAnalysisUpsell,
      pageConfig: {
        topbar: {
          downloadUrls: {
            xlsx: '/download/wur/peer-group-analysis/xlsx',
            csv: '/download/wur/peer-group-analysis/zip',
          },
          filter: {
            hasPeerSelector: true,
          },
        },
      },
    },
    {
      name: 'Year on Year',
      path: 'year-on-year',
      component: YearOnYear,
      layout: DataPlusLayout,
      upsellComponent: YearOnYearUpsell,
      pageConfig: {
        topbar: {
          downloadUrls: {
            xlsx: '/download/wur/year-on-year/xlsx',
            csv: '/download/wur/year-on-year/zip',
          },
        },
      },
    },
    {
      name: 'Institution Finder',
      path: 'institution-finder',
      component: InstitutionFinder,
      upsellComponent: InstitutionFinderUpsell,
      layout: DataPlusLayout,
      pageConfig: {},
    },
  ],
};

export default dataplusConfig;
